import { Link } from "react-router-dom";
import { ShieldCheck, CheckCircle2, ArrowRight } from "lucide-react";

/**
 * SwaanStay Booking Protection Guarantee — legal page.
 * Version 2026-02-v1. Change the version string on the ticket schema if terms change.
 */
export default function BookingProtection() {
  return (
    <div className="min-h-[calc(100vh-4rem)] bg-slate-50 py-10">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <div className="hw-label text-emerald-700">Legal · v2026-02-v1</div>
        <h1 className="hw-head text-4xl md:text-5xl font-black text-slate-900 mt-2 flex items-center gap-3" data-testid="protection-heading">
          <ShieldCheck className="w-10 h-10 text-emerald-600" /> Booking Protection Guarantee
        </h1>
        <p className="text-slate-600 mt-4 leading-relaxed">
          Every booking on SwaanStay is backed by a promise: if a hotel cannot honour your confirmed booking, we&apos;ll make it right. Here&apos;s exactly what that means.
        </p>

        <div className="hw-card p-6 mt-6 border-l-4 border-l-emerald-500">
          <div className="hw-head font-black text-emerald-800 mb-3">Our commitment</div>
          <p className="text-sm text-slate-800 leading-relaxed">
            If you arrive at the hotel with a valid, confirmed SwaanStay booking (paid or wallet-covered) and the hotel refuses check-in due to:
          </p>
          <ul className="mt-3 space-y-2 text-sm text-slate-700">
            <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /> Overbooking (rooms sold twice)</li>
            <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /> Room not available or not as described</li>
            <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /> Hotel temporarily / permanently closed</li>
            <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /> Hotel refuses check-in without valid reason</li>
          </ul>
          <p className="text-sm text-slate-800 mt-3 leading-relaxed">
            SwaanStay will, at our discretion:
          </p>
          <ul className="mt-2 space-y-1 text-sm text-slate-700 list-disc list-inside pl-1">
            <li><span className="font-bold">Find you an alternative hotel</span> of equal or higher category in the same city / area — we cover any price difference.</li>
            <li><span className="font-bold">OR issue a 100% refund</span> of your booking amount within 5 business days.</li>
            <li><span className="font-bold">PLUS credit ₹500 as a SwaanStay wallet inconvenience credit</span> to your account.</li>
          </ul>
        </div>

        {/* Terms */}
        <div className="hw-card p-6 mt-4 space-y-4 text-sm text-slate-800">
          <h2 className="hw-head text-lg font-black text-slate-900">Full terms</h2>
          <ol className="list-decimal list-inside space-y-3 leading-relaxed">
            <li>
              <span className="font-bold">Applies only to confirmed bookings.</span> Bookings still under negotiation, unpaid, or in bid-review status are not covered. A booking is &quot;confirmed&quot; when it appears in your &quot;My Bookings&quot; page with the status &quot;Confirmed&quot; and an invoice has been issued.
            </li>
            <li>
              <span className="font-bold">You must raise a Level-3 ticket within 4 hours</span> of the incident (Support Centre → &quot;Raise a ticket&quot; → check the &quot;Emergency&quot; box). Screenshots of hotel refusal or a call recording help expedite the case.
            </li>
            <li>
              <span className="font-bold">Alternative-hotel arrangement.</span> We aim to arrange the alternative within 3 hours during business hours (9 AM - 9 PM IST). Outside business hours we may issue a full refund instead of arranging an alternative.
            </li>
            <li>
              <span className="font-bold">Refunds.</span> Refunds are credited to the original payment method within 5 business days. UPI refunds typically settle in 24-48 hours; card/net-banking may take 3-5 days per your bank&apos;s policy.
            </li>
            <li>
              <span className="font-bold">Inconvenience credit.</span> The ₹500 wallet credit is a one-time credit per incident, not per person / room. Usable on any future SwaanStay booking within 12 months.
            </li>
            <li>
              <span className="font-bold">Exclusions.</span> The Guarantee does NOT apply if the hotel refused check-in because of:
              <ul className="list-disc list-inside pl-4 mt-1 text-slate-700">
                <li>Government-issued restrictions (police notice, court order, lockdown).</li>
                <li>Force majeure — natural disaster, riot, war, pandemic.</li>
                <li>Documented misconduct by the guest (intoxication, violence, unlisted extra guests).</li>
                <li>Guest failing to provide valid photo ID (as required by Indian law).</li>
                <li>Guest arriving significantly outside the booked check-in window without prior notice.</li>
              </ul>
            </li>
            <li>
              <span className="font-bold">Guest cooperation required.</span> You must cooperate reasonably with our support team, provide any documents we request (booking ID, ID proof, screenshots, call recordings), and remain reachable via the phone number on the booking.
            </li>
            <li>
              <span className="font-bold">Not a substitute for consequential damages.</span> The Guarantee covers the booking amount + inconvenience credit only. SwaanStay is not liable for other consequential losses (missed flights, missed events, hotel bookings for accompanying persons, etc.). We recommend travel insurance for peace of mind.
            </li>
            <li>
              <span className="font-bold">Fair-use.</span> We reserve the right to deny Guarantee claims from accounts with a history of fraudulent or abusive claims, and to modify these terms with 30 days notice via email + in-app notification.
            </li>
            <li>
              <span className="font-bold">Governing law.</span> All disputes under this Guarantee are subject to the exclusive jurisdiction of courts in Bengaluru, Karnataka, India.
            </li>
          </ol>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between hw-card p-4 border-l-4 border-l-blue-500">
          <div className="text-sm text-slate-800">
            <span className="hw-head font-black">Needed to make a claim?</span> Raise an emergency ticket now — we&apos;ll act within the hour.
          </div>
          <Link to="/support/new" className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold px-4 py-2 text-sm" data-testid="protection-raise-claim">
            Raise emergency ticket <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
